// Reveal v3 — "light-led materialize at rest" (#184, ADR-0041 §2 rev. 2026-06-29).
// The reveal is ONE pure timing function over a single clock t (0→1, one beat —
// config.reveal.durationSec). It is LIGHT-LED over the warm studio sweep:
//
//   1. dark (t≈0): the sweep is dim/near-black, the cushion absent;
//   2. the warm light BLOOMS up (lightBloom rises → holds at full) — stage lights
//      rising, the act that replaced the retired sofa wireframe→leather ink-in;
//   3. the finished, full-shape cushion MATERIALIZES (pillowOpacity 0→1) already
//      in its rest pose as the light reaches it — no inflate, no drop, no dissolve;
//   4. CLIMAX: the pet's face RESOLVES in the risen light (faceResolve) — the
//      "that's my dog" recognition beat, landed while the bloom holds at full;
//   5. a soft SETTLE: the contact shadow SPREADS beneath (shadowSpread) to read
//      the cushion's weight, completing exactly at end-of-clock.
//
// Pure + GL-free so the beat is unit-testable; the R3F components that consume it
// (SweepBackdrop's light + dome bloom, LivingPillow's material opacity / pet
// resolve / cast-shadow settle) are verified visually on the real-GPU harness.
// At t = 1 every curve is 1 — the fully-lit resting state that reduced-motion and
// the /shot harness snap straight to with no animation (SETTLED_REVEAL).

export function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

// Smoothstep ease (3t² − 2t³) on the clamped progress, so a curve eases in and out
// instead of running at a constant linear rate.
export function smoothstep01(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}

// A sub-beat that ramps from 0 to 1 over [start, end] (smoothstep-eased) and then
// HOLDS at 1 — and holds at 0 before it begins. This is what lets one clock drive
// several staggered, overlapping curves: each sub-beat owns its own window of t.
function phase(t: number, start: number, end: number): number {
  return smoothstep01((t - start) / (end - start));
}

// The beat choreography, as windows over the single clock t. Overlapping by design
// (the light is still settling as the cushion starts; the cushion is opaque before
// the settle finishes) so the beat reads as one continuous arrival, not four steps.
// The durations are the SHAPE of the reveal; config.reveal.durationSec scales the
// whole clock's wall-time. Tunable here, not frozen — the temporal feel is signed
// off on the real-GPU harness, never the /shot software-GL path (which can't judge
// real-time light + shadow). Invariants the choreography must keep (unit-tested):
//   • the LIGHT leads (BLOOM_START is the earliest), and it reaches full
//     (BLOOM_END) well before the clock ends so the climax happens in full light;
//   • the cushion MATERIALIZES strictly after the bloom begins
//     (MATERIALIZE_START > BLOOM_START) and is opaque before end-of-clock;
//   • the face RESOLVES once the light is well up and lands at the bloom's hold;
//   • the SETTLE is last and is the only curve that finishes AT the clock's end.
const BLOOM_START = 0;
const BLOOM_END = 0.5;
const MATERIALIZE_START = 0.2;
const MATERIALIZE_END = 0.85;
const FACE_START = 0.45;
const FACE_END = 0.9;
const SETTLE_START = 0.6;
const SETTLE_END = 1;

export interface RevealState {
  // The warm stage lighting, dark→full→hold. The R3F layer scales the sweep's
  // key/fill/kicker/accent/ambient + the gradient-dome glow by this, so the whole
  // scene rises from near-black as the bloom climbs.
  lightBloom: number;
  // The cushion's material opacity (0 = not there yet, 1 = the finished pillow),
  // materializing at its fixed rest pose — the cushion never moves with t.
  pillowOpacity: number;
  // The pet's recognition resolve: the face/texture coming up to its true exposure
  // in the risen light at the climax. The R3F layer maps this onto the pet's albedo
  // response (ending at the configured True-Pet exposure at t = 1).
  faceResolve: number;
  // The settle: the soft contact shadow spreading/softening beneath the cushion to
  // read its weight, completing as the clock ends.
  shadowSpread: number;
}

// The light-led materialize-at-rest beat at clock t (clamped, so a scrub or a snap
// past either end is safe). Each curve is an independent, monotonic sub-beat.
export function revealState(t: number): RevealState {
  return {
    lightBloom: phase(t, BLOOM_START, BLOOM_END),
    pillowOpacity: phase(t, MATERIALIZE_START, MATERIALIZE_END),
    faceResolve: phase(t, FACE_START, FACE_END),
    shadowSpread: phase(t, SETTLE_START, SETTLE_END),
  };
}

// The dark start (t = 0) the R3F layer seeds the scene with when a live reveal is
// about to play, so the lights have somewhere to bloom up FROM (no flash of full
// brightness before the beat).
export const INITIAL_REVEAL: RevealState = revealState(0);

// The fully-lit resting state (t = 1). The single source of truth for the snap
// target: reduced-motion, the /shot harness, the empty waiting sweep, and every
// post-reveal idle frame all hold exactly this — no animation.
export const SETTLED_REVEAL: RevealState = revealState(1);
